import type { ReactNode } from 'react';
import type { ClinicInvoiceDTO, ClinicInvoiceLine } from './ClinicInvoiceModal';
import type { Moneda } from '@/shared/billing/currency';
import { isSurchargeKind, surchargePctFromName } from '@/shared/billing/invoiceSurcharges';

/** Nombre a mostrar de una línea: los recargos se traducen por tipo (el guardado está en español). */
export function invoiceLineLabel(
  line: Pick<ClinicInvoiceLine, 'kind' | 'name'>,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (line.kind === 'recargo_mora') {
    return t('pages.clinicInvoices.surcharges.lineMora', { pct: surchargePctFromName(line.name) ?? '' });
  }
  if (line.kind === 'reconexion') {
    return t('pages.clinicInvoices.surcharges.lineReconexion', { pct: surchargePctFromName(line.name) ?? '' });
  }
  return line.name;
}

/** "$ 1.234,00" en pesos, "US$ 1.234,00" en dólares. */
export function formatMoney(value: number, locale: string, moneda: Moneda = 'ARS'): string {
  return `${moneda === 'USD' ? 'US$' : '$'} ${value.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatPeriodLabel(period: string, locale: string): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  const raw = new Date(year, month - 1, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
}

export function periodBounds(period: string, locale: string): { from: string; to: string } {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return { from: period, to: period };
  const from = new Date(year, month - 1, 1);
  const to = new Date(year, month, 0);
  return {
    from: from.toLocaleDateString(locale),
    to: to.toLocaleDateString(locale),
  };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const FIELD_CLASS =
  'w-full rounded border border-slate-200 bg-white px-2 py-1 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-teal-600';

export function InvoiceDocument({
  invoice,
  editing,
  onChangeLine,
  onRemoveLine,
  extraForm,
  t,
  locale,
}: {
  invoice: ClinicInvoiceDTO;
  editing: boolean;
  onChangeLine: (index: number, patch: Partial<ClinicInvoiceLine>) => void;
  onRemoveLine: (index: number) => void;
  extraForm?: ReactNode;
  t: (key: string, opts?: Record<string, unknown>) => string;
  locale: string;
}) {
  const money = (n: number) => formatMoney(n, locale, invoice.moneda);
  const periodLabel = formatPeriodLabel(invoice.period, locale);
  const bounds = periodBounds(invoice.period, locale);
  const due = invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString(locale) : '—';
  const generatedAt = new Date(invoice.generatedAt).toLocaleString(locale);

  return (
    <article className="invoice-sheet mx-auto w-full max-w-[190mm] bg-white px-5 py-6 text-slate-900 sm:px-8 sm:py-8">
      <header className="flex items-start justify-between gap-6 border-b border-slate-200 pb-5">
        <div className="flex min-w-0 items-start gap-3">
          <img
            src="/assets/coofit-logo.png"
            alt="Coofit"
            className="h-9 w-auto shrink-0 rounded bg-black object-contain px-1.5 py-1"
          />
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
              {t('pages.clinicInvoices.docTitle')}
            </p>
            <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">{periodLabel}</p>
            <p className="mt-3 text-sm font-semibold text-slate-900">{invoice.nombre}</p>
            {invoice.email ? <p className="text-xs text-slate-500">{invoice.email}</p> : null}
          </div>
        </div>
        <div className="shrink-0 rounded-md bg-teal-700 px-4 py-3 text-right text-white">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-100">
            {t('pages.clinicInvoices.amountDue')}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">{money(invoice.amountDue)}</p>
          {invoice.previousDebt > 0 ? (
            <p className="mt-1 text-[11px] text-teal-100">
              {t('pages.clinicInvoices.previousDebtIncluded', { amount: money(invoice.previousDebt) })}
            </p>
          ) : null}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 border-b border-slate-200 py-3 text-xs sm:grid-cols-3">
        <MetaItem
          label={t('pages.clinicInvoices.period')}
          value={`${bounds.from} ${t('pages.clinicInvoices.periodTo')} ${bounds.to}`}
        />
        <MetaItem label={t('pages.clinicInvoices.dueDate')} value={due} />
        <MetaItem label={t('pages.clinicInvoices.generatedAt')} value={generatedAt} />
      </div>

      {editing ? (
        <p className="mt-4 text-xs text-amber-800">{t('pages.clinicInvoices.editHint')}</p>
      ) : null}

      <section className="invoice-section mt-7">
        <h2 className="invoice-h2">{t('pages.clinicInvoices.sectionConcepts')}</h2>
        <div className="overflow-x-auto">
          <table className="invoice-table">
            <thead>
              <tr>
                <th>{t('pages.clinicInvoices.concept')}</th>
                <th className="num">{t('pages.clinicInvoices.qty')}</th>
                <th className="num">{t('pages.clinicInvoices.amount')}</th>
                {editing ? <th className="num">{t('common.actions')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((line, i) => {
                const rowEditable = editing && !isSurchargeKind(line.kind);
                return (
                <tr key={`${line.kind}-${i}`}>
                  <td>
                    {rowEditable ? (
                      <div className="space-y-1">
                        <input
                          className={FIELD_CLASS}
                          value={line.name}
                          onChange={(e) => onChangeLine(i, { name: e.target.value })}
                        />
                        <input
                          className={`${FIELD_CLASS} text-xs`}
                          value={line.description ?? ''}
                          onChange={(e) => onChangeLine(i, { description: e.target.value || null })}
                          placeholder={t('pages.clinicInvoices.descriptionPlaceholder')}
                        />
                      </div>
                    ) : (
                      <>
                        <div className={`font-medium ${isSurchargeKind(line.kind) ? 'text-rose-700' : ''}`}>
                          {invoiceLineLabel(line, t)}
                        </div>
                        {line.description ? <div className="mt-0.5 text-[11px] text-slate-500">{line.description}</div> : null}
                        {editing && isSurchargeKind(line.kind) ? (
                          <div className="mt-0.5 text-[11px] text-slate-500">{t('pages.clinicInvoices.surcharges.autoLineHint')}</div>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td className="num">
                    {rowEditable ? (
                      <input
                        type="number"
                        min={0}
                        step="any"
                        className={`${FIELD_CLASS} ml-auto w-16 text-right`}
                        value={line.quantity}
                        onChange={(e) => onChangeLine(i, { quantity: Number(e.target.value) || 0 })}
                      />
                    ) : (
                      line.quantity
                    )}
                  </td>
                  <td className="num font-medium">
                    {rowEditable ? (
                      <input
                        type="number"
                        step="0.01"
                        className={`${FIELD_CLASS} ml-auto w-28 text-right`}
                        value={line.subtotal}
                        onChange={(e) => {
                          const next = Number(e.target.value) || 0;
                          const qty = line.quantity || 1;
                          onChangeLine(i, { unitPrice: next / qty });
                        }}
                      />
                    ) : (
                      money(line.subtotal)
                    )}
                  </td>
                  {editing ? (
                    <td className="num">
                      {rowEditable ? (
                        <button
                          type="button"
                          className="text-xs text-rose-600 hover:underline disabled:text-slate-300"
                          onClick={() => onRemoveLine(i)}
                          disabled={invoice.lines.filter((l) => !isSurchargeKind(l.kind)).length <= 1}
                        >
                          {t('common.delete')}
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>{t('pages.clinicInvoices.invoiceTotal')}</td>
                <td className="num">{money(invoice.total)}</td>
                {editing ? <td /> : null}
              </tr>
            </tfoot>
          </table>
        </div>
        {extraForm}
      </section>

      <footer className="invoice-footer mt-8 flex flex-col gap-2 border-t border-slate-200 pt-3 text-[10px] leading-relaxed text-slate-400 sm:flex-row sm:items-end sm:justify-between">
        <p className="max-w-sm">{t('pages.clinicInvoices.disclaimer')}</p>
        <p>
          {invoice.nombre} · {t('pages.clinicInvoices.docTitle')}
        </p>
        <p>{t('pages.clinicInvoices.pageOf')}</p>
      </footer>

      <style>{`
        .invoice-h2 { font-size: 11px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: #0f766e; margin: 0 0 10px; }
        .invoice-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .invoice-table th { text-align: left; font-size: 10px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: #64748b; border-bottom: 1px solid #e2e8f0; padding: 0 8px 8px 0; }
        .invoice-table td { padding: 9px 8px 9px 0; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
        .invoice-table .num { text-align: right; font-variant-numeric: tabular-nums; }
        .invoice-table tfoot td { border-bottom: 0; border-top: 1px solid #cbd5e1; font-weight: 600; padding-top: 10px; }
      `}</style>
    </article>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm text-slate-700">{value}</p>
    </div>
  );
}

export function buildPrintHtml(
  invoice: ClinicInvoiceDTO,
  locale: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const money = (n: number) => formatMoney(n, locale, invoice.moneda);
  const periodLabel = formatPeriodLabel(invoice.period, locale);
  const bounds = periodBounds(invoice.period, locale);
  const due = invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString(locale) : '—';
  const generatedAt = new Date(invoice.generatedAt).toLocaleString(locale);
  const logoSrc = `${window.location.origin}/assets/coofit-logo.png`;
  const previousDebt =
    invoice.previousDebt > 0
      ? `<div class="debt">${escapeHtml(t('pages.clinicInvoices.previousDebtIncluded', { amount: money(invoice.previousDebt) }))}</div>`
      : '';

  const conceptRows = invoice.lines
    .map(
      (line) => `<tr>
        <td>${escapeHtml(invoiceLineLabel(line, t))}${line.description ? `<div class="desc">${escapeHtml(line.description)}</div>` : ''}</td>
        <td class="num">${line.quantity}</td>
        <td class="num">${money(line.subtotal)}</td>
      </tr>`
    )
    .join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(t('pages.clinicInvoices.docTitle'))} — ${escapeHtml(invoice.nombre)}</title>
  <style>
    @page { size: A4 portrait; margin: 10mm 12mm 12mm 12mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; color: #0f172a; font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; }
    body { font-size: 12.5px; line-height: 1.4; }
    header { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; border-bottom: 1px solid #e2e8f0; padding-bottom: 16px; }
    .brand { display: flex; gap: 12px; align-items: flex-start; min-width: 0; }
    .brand img { height: 36px; width: auto; background: #000; padding: 4px 8px; border-radius: 4px; }
    .kicker { font-size: 10px; font-weight: 600; letter-spacing: .12em; text-transform: uppercase; color: #64748b; margin: 0; }
    .period { font-size: 18px; font-weight: 650; margin: 2px 0 10px; }
    .clinic { font-size: 13px; font-weight: 650; margin: 0; }
    .email { font-size: 11px; color: #64748b; margin: 2px 0 0; }
    .total { background: #0f766e; color: #fff; padding: 12px 16px; border-radius: 6px; text-align: right; min-width: 180px; }
    .total span { display: block; font-size: 9px; letter-spacing: .14em; text-transform: uppercase; color: #ccfbf1; }
    .total strong { display: block; margin-top: 4px; font-size: 24px; letter-spacing: -.03em; font-variant-numeric: tabular-nums; }
    .debt { margin-top: 4px; font-size: 10px; color: #ccfbf1; }
    .meta { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; border-bottom: 1px solid #e2e8f0; padding: 10px 0 12px; }
    .meta p { margin: 0; }
    .meta .lbl { font-size: 9px; font-weight: 650; letter-spacing: .1em; text-transform: uppercase; color: #94a3b8; }
    .meta .val { margin-top: 2px; font-size: 12px; color: #334155; }
    h2 { font-size: 10px; font-weight: 650; letter-spacing: .08em; text-transform: uppercase; color: #0f766e; margin: 22px 0 8px; page-break-after: avoid; break-after: avoid; }
    table { width: 100%; border-collapse: collapse; }
    th { text-align: left; font-size: 9px; font-weight: 650; letter-spacing: .08em; text-transform: uppercase; color: #64748b; border-bottom: 1px solid #e2e8f0; padding: 0 6px 6px 0; }
    td { padding: 7px 6px 7px 0; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .desc { margin-top: 2px; font-size: 10px; color: #64748b; }
    tfoot td { border-bottom: 0; border-top: 1px solid #cbd5e1; font-weight: 650; padding-top: 8px; }
    footer { display: flex; justify-content: space-between; gap: 16px; border-top: 1px solid #e2e8f0; margin-top: 22px; padding-top: 10px; color: #94a3b8; font-size: 9px; line-height: 1.45; page-break-inside: avoid; break-inside: avoid; }
    footer p { margin: 0; }
    .section { page-break-inside: auto; }
    tr, h2 { page-break-inside: avoid; break-inside: avoid; }
    thead { display: table-header-group; }
    @media print {
      html, body { background: #fff; }
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  </style>
</head>
<body>
  <header>
    <div class="brand">
      <img src="${escapeHtml(logoSrc)}" alt="Coofit" />
      <div>
        <p class="kicker">${escapeHtml(t('pages.clinicInvoices.docTitle'))}</p>
        <p class="period">${escapeHtml(periodLabel)}</p>
        <p class="clinic">${escapeHtml(invoice.nombre)}</p>
        ${invoice.email ? `<p class="email">${escapeHtml(invoice.email)}</p>` : ''}
      </div>
    </div>
    <div class="total">
      <span>${escapeHtml(t('pages.clinicInvoices.amountDue'))}</span>
      <strong>${money(invoice.amountDue)}</strong>
      ${previousDebt}
    </div>
  </header>
  <div class="meta">
    <div><p class="lbl">${escapeHtml(t('pages.clinicInvoices.period'))}</p><p class="val">${escapeHtml(bounds.from)} ${escapeHtml(t('pages.clinicInvoices.periodTo'))} ${escapeHtml(bounds.to)}</p></div>
    <div><p class="lbl">${escapeHtml(t('pages.clinicInvoices.dueDate'))}</p><p class="val">${escapeHtml(due)}</p></div>
    <div><p class="lbl">${escapeHtml(t('pages.clinicInvoices.generatedAt'))}</p><p class="val">${escapeHtml(generatedAt)}</p></div>
  </div>
  <section class="section">
    <h2>${escapeHtml(t('pages.clinicInvoices.sectionConcepts'))}</h2>
    <table>
      <thead><tr>
        <th>${escapeHtml(t('pages.clinicInvoices.concept'))}</th>
        <th class="num">${escapeHtml(t('pages.clinicInvoices.qty'))}</th>
        <th class="num">${escapeHtml(t('pages.clinicInvoices.amount'))}</th>
      </tr></thead>
      <tbody>${conceptRows}</tbody>
      <tfoot><tr>
        <td colspan="2">${escapeHtml(t('pages.clinicInvoices.invoiceTotal'))}</td>
        <td class="num">${money(invoice.total)}</td>
      </tr></tfoot>
    </table>
  </section>
  <footer>
    <p>${escapeHtml(t('pages.clinicInvoices.disclaimer'))}</p>
    <p>${escapeHtml(invoice.nombre)} · ${escapeHtml(t('pages.clinicInvoices.docTitle'))}</p>
    <p>${escapeHtml(t('pages.clinicInvoices.pageOf'))}</p>
  </footer>
</body>
</html>`;
}
